import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { adminUserService } from '@/services/adminUserService';
import { Loader2 } from 'lucide-react';

interface UserDetailHistoryProps {
  userId: string;
}

interface SubscriptionEvent {
  id: number;
  date: string;
  event_type: string;
  event_source: string;
  description?: string;
  tier?: string;
  previous_tier?: string;
  status?: string;
  billing_cycle?: string;
  amount?: string;
  currency?: string;
  initiated_by?: string;
}

const UserDetailHistory: React.FC<UserDetailHistoryProps> = ({ userId }) => {
  const [events, setEvents] = useState<SubscriptionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadHistory();
  }, [userId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await adminUserService.getUserSubscriptionHistory(userId, 50, 0);
      setEvents(response.events);
      setTotal(response.total);
      setError(null);
    } catch (err: any) {
      console.error('Error loading subscription history:', err);
      setError('Failed to load subscription history');
    } finally {
      setLoading(false);
    }
  };

  const formatEventType = (eventType: string): string => {
    return eventType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatAmount = (amount?: string, currency?: string): string | null => {
    if (!amount) return null;
    const numAmount = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(numAmount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventBadgeVariant = (eventType: string): "default" | "secondary" | "destructive" | "outline" => {
    if (eventType.includes('failed') || eventType.includes('cancelled')) return 'destructive';
    if (eventType.includes('activated') || eventType.includes('payment_received')) return 'default';
    return 'secondary';
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription History</CardTitle>
          <CardDescription>Recent subscription events</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading history...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription History</CardTitle>
          <CardDescription>Recent subscription events</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive">
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription History</CardTitle>
          <CardDescription>Recent subscription events</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">No subscription history</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription History</CardTitle>
        <CardDescription>Recent subscription events ({total} total)</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {events.map((event) => (
            <div
              key={event.id}
              className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={getEventBadgeVariant(event.event_type)}>
                    {formatEventType(event.event_type)}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {event.event_source}
                  </Badge>
                </div>
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {formatDate(event.date)}
                </span>
              </div>

              {event.description && (
                <p className="text-sm mb-2">{event.description}</p>
              )}

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {event.tier && (
                  <span>
                    Tier: <span className="font-mono text-foreground">{event.tier}</span>
                    {event.previous_tier && event.previous_tier !== event.tier && (
                      <span className="ml-1">(from {event.previous_tier})</span>
                    )}
                  </span>
                )}
                {event.amount && (
                  <span>
                    Amount: <span className="font-mono text-foreground">{formatAmount(event.amount, event.currency)}</span>
                  </span>
                )}
                {event.billing_cycle && (
                  <span>
                    Cycle: <span className="text-foreground">{event.billing_cycle}</span>
                  </span>
                )}
                {event.initiated_by && (
                  <span>
                    By: <span className="text-foreground">{event.initiated_by}</span>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {events.length < total && (
          <div className="text-center mt-6">
            <button
              onClick={() => {/* TODO: Load more */}}
              className="text-sm text-primary hover:text-primary/80 transition-colors"
            >
              Load more events ({events.length} of {total})
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserDetailHistory;
